import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Shield, CheckCircle, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VerifyPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState('initiate'); // 'initiate', 'verify', 'success'
  const [sessionData, setSessionData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [idNumber, setIdNumber] = useState('');
  const [userType, setUserType] = useState('');
  const token = localStorage.getItem('mojo_token');
  const user = JSON.parse(localStorage.getItem('mojo_user') || '{}');

  useEffect(() => {
    const id = searchParams.get('id_number') || user.id_number;
    const type = searchParams.get('user_type') || user.user_type;
    
    if (id) setIdNumber(id);
    if (type) setUserType(type);

    if (!token) {
      toast.error('Authentication required');
      navigate('/');
    }
  }, [searchParams, navigate, token, user.id_number, user.user_type]);

  const handleInitiate = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/biometric/initiate`,
        { 
          id_number: idNumber,
          return_url: window.location.origin 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.status === 'success') {
        setSessionData(response.data);
        setStep('verify');
        toast.success('Verification session created');
      }
    } catch (error) {
      let errorMsg = 'Failed to initiate verification';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMsg = detail;
        } else if (Array.isArray(detail)) {
          errorMsg = detail.map(e => e.msg).join(', ');
        }
      }
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenVerification = () => {
    if (!sessionData?.verification_url) return;

    const width = 800;
    const height = 900;
    const left = (window.screen.width / 2) - (width / 2);
    const top = (window.screen.height / 2) - (height / 2);
    
    const verifyWindow = window.open(
      sessionData.verification_url,
      'SelfIDVerification',
      `width=${width},height=${height},top=${top},left=${left}`
    );

    toast.info('Complete face capture and ID verification in the popup window');

    const pollTimer = setInterval(() => {
      if (verifyWindow && verifyWindow.closed) {
        clearInterval(pollTimer);
        toast.info('Verification window closed. Click "Complete Verification" when ready.');
      }
    }, 1000);
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/biometric/verify`,
        {
          id_number: idNumber,
          access_token: sessionData.access_token
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.verified) {
        setStep('success');
        toast.success(`Verification successful! Score: ${response.data.verification_score}%`);
        
        // Update local storage
        const updatedUser = { ...user, biometric_verified: true };
        localStorage.setItem('mojo_user', JSON.stringify(updatedUser));
        
        // Redirect after 3 seconds
        setTimeout(() => {
          const redirectPath = userType === 'merchant' ? '/merchant' : '/customer';
          navigate(redirectPath);
        }, 3000);
      } else {
        toast.error('Verification failed. Please try again.');
      }
    } catch (error) {
      let errorMsg = 'Verification completion failed';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMsg = detail;
        } else if (Array.isArray(detail)) {
          errorMsg = detail.map(e => e.msg).join(', ');
        }
      }
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FAF5F0] via-[#F5EDE4] to-[#F5E8D8] flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <CardTitle className="text-2xl text-[#1B0C34] flex items-center gap-2">
            <Shield className="w-8 h-8 text-[#F2B10C]" />
            ID Verification
          </CardTitle>
          <CardDescription>Complete your ID verification using VirtuallVerify</CardDescription>
        </CardHeader>
        <CardContent>
          {step === 'initiate' && (
            <div className="space-y-6">
              <div className="p-4 bg-[#F2B10C]/10 border border-[#F2B10C]/30 rounded-lg">
                <h3 className="font-semibold text-[#1B0C34] mb-2">What you will need:</h3>
                <ul className="space-y-2 text-sm text-[#1B0C34]/80">
                  <li className="flex items-start gap-2">
                    <span className="text-[#145539] font-bold">✓</span>
                    <span>Your South African ID card or passport</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#145539] font-bold">✓</span>
                    <span>Good lighting for face capture</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#145539] font-bold">✓</span>
                    <span>Clear photo of your ID document</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#145539] font-bold">✓</span>
                    <span>Minimum 80% match score required</span>
                  </li>
                </ul>
              </div>

              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Verification ID:</strong> {idNumber || 'Not provided'}
                </p>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => navigate(userType === 'merchant' ? '/merchant' : '/customer')}
                  className="flex-1"
                >
                  <ArrowLeft className="mr-2 w-4 h-4" />
                  Back to Dashboard
                </Button>
                <Button
                  onClick={handleInitiate}
                  disabled={loading}
                  className="flex-1 bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34] font-semibold"
                >
                  {loading ? 'Initializing...' : 'Start Verification'}
                </Button>
              </div>
            </div>
          )}

          {step === 'verify' && (
            <div className="space-y-6">
              <div className="p-4 bg-[#145539]/10 border border-[#145539] rounded-lg">
                <p className="text-sm text-[#145539] font-medium mb-2">✓ Verification session created</p>
                <p className="text-xs text-[#1B0C34]/70">
                  Session ID: {sessionData?.access_token?.substring(0, 20)}...
                </p>
              </div>

              <div className="space-y-3">
                <Button
                  onClick={handleOpenVerification}
                  className="w-full bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34] font-semibold"
                  size="lg"
                >
                  Open VirtuallVerify Portal
                </Button>

                <p className="text-xs text-center text-[#1B0C34]/60">
                  A popup window will open. Complete the face capture and ID verification process.
                </p>
              </div>

              <div className="pt-4 border-t">
                <p className="text-sm text-[#1B0C34] mb-3">After completing verification in the popup:</p>
                <Button
                  onClick={handleComplete}
                  disabled={loading}
                  className="w-full bg-[#145539] hover:bg-[#1e7049] text-white font-semibold"
                >
                  {loading ? 'Verifying...' : 'Complete Verification'}
                </Button>
              </div>

              <Button
                variant="outline"
                onClick={() => navigate(userType === 'merchant' ? '/merchant' : '/customer')}
                className="w-full"
              >
                Cancel & Return
              </Button>
            </div>
          )}

          {step === 'success' && (
            <div className="space-y-6 text-center">
              <div className="flex justify-center">
                <CheckCircle className="w-24 h-24 text-[#145539]" />
              </div>
              
              <div>
                <h3 className="text-2xl font-bold text-[#1B0C34] mb-2">Verification Successful!</h3>
                <p className="text-[#1B0C34]/70">
                  Your ID has been verified. You can now perform all transactions.
                </p>
              </div>

              <div className="p-4 bg-[#145539]/10 border border-[#145539] rounded-lg">
                <p className="text-sm text-[#145539]">
                  Redirecting to dashboard in 3 seconds...
                </p>
              </div>

              <Button
                onClick={() => navigate(userType === 'merchant' ? '/merchant' : '/customer')}
                className="bg-[#145539] hover:bg-[#1e7049] text-white"
              >
                Go to Dashboard Now
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VerifyPage;
